import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { calculatePayoutPercentages } from "../utility/calculatePayouts";
import { materializePlaceAmounts } from "../utility/generateShapedPayoutTable";

export const useEventPayouts = (eventIds, tournamentId) => {
  const [eventDetails, setEventDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const memoizedEventIds = useMemo(() => eventIds, [JSON.stringify(eventIds)]);

  const fetchEventDetails = useCallback(async () => {
    if (!memoizedEventIds?.length || !tournamentId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: tournament, error: tournamentError } = await supabase
        .from("tournaments")
        .select(
          `
          id,
          payoutRatioNumerator,
          payoutRatioDenominator,
          payoutTableId
        `
        )
        .eq("id", tournamentId)
        .single();

      if (tournamentError) throw tournamentError;

      // Fetch verification status from the new system
      const { data: verificationStates, error: verificationError } =
        await supabase
          .from("verificationStates")
          .select("*")
          .eq("tournamentId", tournamentId);

      if (verificationError) {
        console.warn(
          "Could not fetch tournament verification status:",
          verificationError
        );
      }

      // Note: Verification status is fetched but not currently used in this hook
      // These variables are available if needed for future functionality

      // Only fetch payout data if payoutTableId exists
      let payoutTable = [];
      let roundToWholeDollars = true;
      let lastPlaceFloor = null;
      let ratioNumerator = tournament.payoutRatioNumerator;
      let ratioDenominator = tournament.payoutRatioDenominator;
      if (tournament.payoutTableId) {
        const { data: payoutTableRow, error: payoutTableRowError } =
          await supabase
            .from("payoutTable")
            .select(
              "roundToWholeDollars, lastPlaceMustGet, payoutRatioNumerator, payoutRatioDenominator"
            )
            .eq("id", tournament.payoutTableId)
            .maybeSingle();

        if (payoutTableRowError) throw payoutTableRowError;
        if (payoutTableRow) {
          roundToWholeDollars = payoutTableRow.roundToWholeDollars !== false;
          if (Number(payoutTableRow.lastPlaceMustGet) > 0) {
            lastPlaceFloor = Number(payoutTableRow.lastPlaceMustGet);
          }
          ratioNumerator =
            payoutTableRow.payoutRatioNumerator ?? ratioNumerator;
          ratioDenominator =
            payoutTableRow.payoutRatioDenominator ?? ratioDenominator;
        }
        const { data: payoutTableData, error: payoutTableError } =
          await supabase
            .from("payoutData")
            .select(
              `
            payoutQuantity,
            percentage,
            payoutOrder
          `
            )
            .eq("payoutTableId", tournament.payoutTableId)
            .order("payoutOrder", { ascending: true });

        if (payoutTableError) throw payoutTableError;
        payoutTable = payoutTableData || [];
      }

      // Use RPC function to get event statistics (server-side calculation)
      const { data: eventStats, error: statsError } = await supabase.rpc(
        "get_event_entry_statistics",
        {
          input_tournament_id: tournamentId,
        }
      );

      if (statsError) throw statsError;

      // Filter to only requested events
      const filteredStats = eventStats.filter((stat) =>
        memoizedEventIds.includes(stat.event_id)
      );

      // Get all event configurations to identify all events
      const { data: allEventConfigs, error: allEventConfigsError } =
        await supabase.rpc("get_all_event_configurations_for_tournament", {
          input_tournament_id: tournamentId,
        });

      if (allEventConfigsError) {
        console.warn("Error fetching all event configs:", allEventConfigsError);
      }

      const allEventIds = new Set(
        (allEventConfigs || []).map((config) => config.all_event_id)
      );

      // Get event details for payout ratio override
      const { data: events, error: eventsError } = await supabase
        .from("tournamentEvents")
        .select(
          `
          id,
          eventName,
          isPrimary,
          payoutRatioOverride,
          bowlerQuantity
        `
        )
        .in("id", memoizedEventIds);

      if (eventsError) throw eventsError;

      // Fetch existing payouts from tournamentPayouts
      const { data: existingPayouts, error: payoutsError } = await supabase
        .from("tournamentPayouts")
        .select("*")
        .in("eventID", memoizedEventIds)
        .order("place", { ascending: true });

      if (payoutsError) throw payoutsError;

      const processedEvents = {};

      // Get unique payout quantities from the payout table
      const uniquePayoutQuantities = [
        ...new Set(payoutTable.map((p) => p.payoutQuantity)),
      ].sort((a, b) => a - b);

      // Process each event using server-calculated statistics
      filteredStats.forEach((stat) => {
        const event = events.find((e) => e.id === stat.event_id);
        if (!event) return;

        const isAllEvent = allEventIds.has(stat.event_id);
        // For all events: always use bowler_count (unique bowlers) for everything
        // For regular events: use total_entries (teams)
        const totalBowlers = stat.bowler_count; // Unique bowlers for all events, total entries for regular
        const totalEntries = stat.total_entries; // Total entries for all events, teams for regular
        const displayEntries = isAllEvent ? totalBowlers : totalEntries; // What to show in UI
        const prizeFund = stat.prize_fund;

        const payoutRatio =
          tournament.payoutRatioNumerator / tournament.payoutRatioDenominator;
        // For all events: use unique bowlers for payout ratio calculation
        // For regular events: use total entries (teams)
        const entriesForPayoutRatio = isAllEvent ? totalBowlers : totalEntries;
        const defaultPlacesToPay = Math.ceil(entriesForPayoutRatio * payoutRatio);

        // Calculate all possible payout arrays
        const possiblePayouts = uniquePayoutQuantities
          .map((quantity) => {
            const payoutPercentages = calculatePayoutPercentages(
              quantity,
              payoutTable
            );
            if (!payoutPercentages) return null;

            const places = payoutPercentages.map((payout) => ({
              place: payout.place,
              percentage: payout.percentage / 100,
            }));
            const paid = materializePlaceAmounts({
              places,
              prizeFund,
              roundToWholeDollars,
              lastPlaceFloor,
              ratioNumerator,
              ratioDenominator,
            });
            return {
              quantity,
              payouts: places.map((payout, index) => ({
                place: payout.place,
                percentage: payout.percentage,
                amount: paid[index]?.amount ?? 0,
              })),
            };
          })
          .filter(Boolean);

        // Check if we have existing payouts for this event
        const eventPayouts = existingPayouts?.filter(
          (p) => p.eventID === event.id
        );
        let currentPayoutQuantity =
          event.payoutRatioOverride || defaultPlacesToPay;
        let selectedPayout = null;

        if (eventPayouts?.length > 0) {
          // If we have existing payouts, use those
          selectedPayout = {
            quantity: eventPayouts.length,
            payouts: eventPayouts
              .map((p) => ({
                place: p.place,
                percentage: p.percentage, // Already stored as decimal
                amount: p.amount,
              }))
              .sort((a, b) => a.place - b.place), // Ensure sorted by place
            isFromDatabase: true, // Flag to indicate these are saved payouts
          };
          currentPayoutQuantity = eventPayouts.length;
        } else {
          // Otherwise use the default or override
          selectedPayout = possiblePayouts.find(
            (p) => p.quantity === currentPayoutQuantity
          );
          if (selectedPayout) {
            selectedPayout = {
              ...selectedPayout,
              payouts: selectedPayout.payouts.map((payout) => ({
                ...payout,
                percentage:
                  prizeFund > 0 ? payout.amount / prizeFund : payout.percentage,
              })),
              isFromDatabase: false,
            };
          }
        }

        processedEvents[stat.event_id] = {
          id: stat.event_id,
          eventName: stat.event_name,
          isPrimary: stat.is_primary,
          totalEntries: displayEntries, // Show unique bowlers for all events, teams for regular
          totalBowlers,
          prizeFund,
          defaultPlacesToPay,
          possiblePayouts,
          currentPayoutQuantity,
          selectedPayout,
          roundToWholeDollars,
          hasPayoutTable: Boolean(tournament.payoutTableId),
          lastPlaceFloor,
          ratioNumerator,
          ratioDenominator,
        };
      });

      setEventDetails(processedEvents);
    } catch (error) {
      console.error("Error fetching event details:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [memoizedEventIds, tournamentId]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  return {
    eventDetails,
    loading,
    error,
    refreshEventDetails: fetchEventDetails,
  };
};
